import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../UserContext';
import TemplateKeyField, { keysDiffer } from './TemplateKeyField';
import { backendTemplateApi } from './cloudReportApi';
import { REPORT_GROUPS, resolveClientSex } from './reportCodes';
import {
  CS_VALUES,
  allPermutationKeys,
  canEditInterpretationTemplates,
  lookupProse,
  makeCautionKey,
  missingTemplateKeys,
  nearestTemplateKeys,
  stepMissingKey,
} from './reportTemplates';

const DUPLICATE_SUGGESTION_LIMIT = 5;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function useClaims(claimsProp) {
  const user = useContext(UserContext);
  const [claims, setClaims] = useState(claimsProp ?? null);
  useEffect(() => {
    if (claimsProp) {
      setClaims(claimsProp);
      return undefined;
    }
    if (!user?.getIdTokenResult) {
      setClaims(null);
      return undefined;
    }
    let active = true;
    user.getIdTokenResult()
      .then((result) => { if (active) setClaims(result.claims ?? null); })
      .catch(() => { if (active) setClaims(null); });
    return () => { active = false; };
  }, [user, claimsProp]);
  return claims;
}

/**
 * The shared interpretation template library (draft -> publish) for template
 * owners. This changes the paragraphs every future report starts from; it
 * never changes a report that already exists. Per-report wording is edited
 * in the report editor instead. No scoring happens here: keys are browsed
 * from the full catalog.
 */
export default function CloudTemplateCms({ client, api = backendTemplateApi, claims: claimsProp }) {
  const user = useContext(UserContext);
  const claims = useClaims(claimsProp);
  const sex = resolveClientSex(client);
  const canEdit = canEditInterpretationTemplates(claims);
  const [templates, setTemplates] = useState({ published: null, draft: null });
  const [draftKeys, setDraftKeys] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [catalogGroup, setCatalogGroup] = useState('CS');
  const [catalogKey, setCatalogKey] = useState(null);
  const [unlockedKey, setUnlockedKey] = useState(null);
  const [justSavedKey, setJustSavedKey] = useState(null);
  const [previewed, setPreviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    if (!sex) return undefined;
    setLoading(true);
    setError(null);
    api.loadTemplatesForSex(sex)
      .then((loaded) => {
        if (!active) return;
        setTemplates(loaded);
        setDraftKeys(loaded.draft?.keys ?? loaded.published?.keys ?? {});
      })
      .catch((loadError) => {
        console.error('Failed to load interpretation templates:', loadError);
        if (active) setError('Failed to load interpretation templates.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, sex]);

  const publishedKeys = templates.published?.keys ?? {};
  const workingKeys = canEdit ? draftKeys : publishedKeys;
  const catalogKeys = useMemo(
    () => (catalogGroup === 'CS' ? CS_VALUES.map((cs) => makeCautionKey(cs)) : allPermutationKeys(catalogGroup)),
    [catalogGroup]
  );
  const catalogMissing = useMemo(() => missingTemplateKeys(workingKeys, catalogKeys), [workingKeys, catalogKeys]);
  const missingSet = useMemo(() => new Set(catalogMissing), [catalogMissing]);
  const hasDirtyDraft = canEdit && keysDiffer(draftKeys, publishedKeys);
  const blockedTitle = unlockedKey ? 'Save or cancel the open field first' : undefined;

  async function handleFieldSave(key, value) {
    if (!canEdit) return;
    const next = { ...draftKeys };
    if (value && value.trim()) next[key] = value;
    else delete next[key];
    setBusy(true);
    setStatus(null);
    try {
      await api.saveDraft({ sex, keys: next, userId: user?.uid });
      setDraftKeys(next);
      setUnlockedKey(null);
      setJustSavedKey(key);
      setPreviewed(false);
    } catch (saveError) {
      console.error('Failed to save draft:', saveError);
      setStatus({ tone: 'error', text: 'Could not save the draft.' });
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!canEdit || !previewed) return;
    setBusy(true);
    setStatus(null);
    try {
      await api.saveDraft({ sex, keys: draftKeys, userId: user?.uid });
      const published = await api.publishDraft({ sex, userId: user?.uid });
      setTemplates({ published: { ...published, keys: draftKeys }, draft: { sex, keys: draftKeys } });
      setPreviewed(false);
      setStatus({
        tone: 'ok',
        text: `Published ${sex} template v${published.version}. Reports generated from now on use it; existing reports are unchanged.`,
      });
    } catch (publishError) {
      console.error('Failed to publish draft:', publishError);
      setStatus({ tone: 'error', text: publishError.message || 'Could not publish the draft.' });
    } finally {
      setBusy(false);
    }
  }

  if (!sex) {
    return <p className="text-sm text-gray-500">The client has no sex on file, so there is no template library to show.</p>;
  }

  return (
    <section className="space-y-4" aria-label="Interpretation template library">
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-semibold">Shared template library ({sex})</p>
        <p className="mt-1">
          Changes here affect the paragraphs used for every future {sex} report. They do not change this
          client&apos;s report. To change wording for one client, use the report editor below the charts.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
        <span className="flex items-center gap-2">
          {loading ? 'Loading templates…' : `Published: ${templates.published?.version != null ? `v${templates.published.version}` : 'none'}`}
          {hasDirtyDraft && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Draft</span>
          )}
        </span>
        {!canEdit && <span>Only template owners can edit or publish templates.</span>}
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Group</span>
          <select
            className="rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm"
            disabled={Boolean(unlockedKey)}
            onChange={(event) => {
              setCatalogGroup(event.target.value);
              setCatalogKey(null);
            }}
            title={blockedTitle}
            value={catalogGroup}
          >
            <option value="CS">Caution (CS)</option>
            {REPORT_GROUPS.map((group) => (
              <option key={group.id} value={group.id}>{group.label}</option>
            ))}
          </select>
        </label>
        <p className="text-sm text-gray-500">{catalogMissing.length} missing of {catalogKeys.length}</p>
        {catalogMissing.length > 0 && [['Previous missing', -1], ['Next missing', 1]].map(([label, direction]) => (
          <button
            className="rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
            disabled={Boolean(unlockedKey)}
            key={label}
            onClick={() => {
              const next = stepMissingKey({ ordered: catalogKeys, missing: catalogMissing, current: catalogKey, direction });
              if (next) setCatalogKey(next);
            }}
            title={blockedTitle}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex max-h-48 flex-wrap gap-2 overflow-auto rounded-md border border-gray-200 p-2">
        {catalogKeys.map((key) => (
          <button
            aria-pressed={catalogKey === key}
            className={classNames(
              'rounded-md px-2 py-1 text-xs ring-1 ring-inset disabled:opacity-40',
              catalogKey === key
                ? 'bg-indigo-600 text-white ring-indigo-600'
                : missingSet.has(key)
                  ? 'bg-amber-50 text-amber-900 ring-amber-300 hover:bg-amber-100'
                  : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            )}
            disabled={Boolean(unlockedKey)}
            key={key}
            onClick={() => setCatalogKey(key)}
            title={blockedTitle ?? (missingSet.has(key) ? 'No paragraph yet' : undefined)}
            type="button"
          >
            {key}
          </button>
        ))}
      </div>

      {catalogKey && (
        <TemplateKeyField
          busy={busy}
          canEdit={canEdit}
          duplicateCandidates={canEdit ? nearestTemplateKeys(draftKeys, catalogKey, { limit: DUPLICATE_SUGGESTION_LIMIT }) : []}
          justSaved={justSavedKey === catalogKey}
          key={catalogKey}
          keyName={catalogKey}
          lockedElsewhere={Boolean(unlockedKey) && unlockedKey !== catalogKey}
          missing={!lookupProse(workingKeys, catalogKey)}
          onCancel={() => setUnlockedKey(null)}
          onSave={(value) => handleFieldSave(catalogKey, value)}
          onUnlock={() => {
            if (unlockedKey && unlockedKey !== catalogKey) return false;
            setJustSavedKey(null);
            setUnlockedKey(catalogKey);
            return true;
          }}
          published={lookupProse(publishedKeys, catalogKey)}
          savedValue={lookupProse(workingKeys, catalogKey)}
          unlocked={unlockedKey === catalogKey}
        />
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input checked={previewed} onChange={(event) => setPreviewed(event.target.checked)} type="checkbox" />
            I reviewed this draft
          </label>
          <button
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300"
            disabled={busy || !previewed || Boolean(unlockedKey)}
            onClick={handlePublish}
            type="button"
          >
            Publish new template version
          </button>
        </div>
      )}

      {status && (
        <p className={classNames('text-sm', status.tone === 'error' ? 'text-red-700' : 'text-green-700')} role="status">
          {status.text}
        </p>
      )}
    </section>
  );
}
